import React, { useEffect } from 'react';
import Catalog from './pages/Catalog';
import styled, { createGlobalStyle } from 'styled-components';
import "@fontsource/ibm-plex-mono";
import Footer from './components/Footer';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import HomePage from './pages/HomePage';
import DeliveryConditions from './pages/DeliveryConditions';
import Header from './components/Header';
import Admin from './admin/Admin';
import ModalContainer from './components/ModalContainer';
import AuthorsListView from './admin/AuthorsListView';
import BookListView from './admin/BookListView';
import GenresListView from './admin/GenresListView';
import OrdersListView from './admin/OrdersListView';
import NewsListView from './admin/NewsListView';
import BookDetailPage from './pages/BookDetailPage';
import FormatsListView from './admin/FormatsListView';
import LanguagesListView from './admin/LanguagesListView';
import PublisherListView from './admin/publishersListView';
import CoverTypeListView from './admin/CoverTypesListView';
//import DbTables from './admin/dbTables';
import BookFormView from './admin/BookFormView';
import { clearAccessToken, setAccessToken, setIsAuthChecked } from './redux/slices/authSlice';
import { useAuthDataSetter, useDispatch } from './redux/hooks';
import ProtectedRoute from './routes/ProtectedRoute';
import UserProfile from './components/UserProfile';
import EmailVerification from './components/EmailVerification';
import OrderPage from './pages/OrderPage';
import TermsOfUse from './pages/TermsOfUse';
import StoreSecurity from './pages/StoreSecurity';
import { useSelector } from 'react-redux';
import { RootState } from '../src/redux/store';
import { addToCart } from '../src/redux/slices/cartSlice';
import { addToFavorites } from '../src/redux/slices/favoritesSlice';
import { FavoriteItem } from '../src/types/book';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: "IBM Plex Mono", 'Roboto', sans-serif;
  }
  
  body {
    background-color: #f5f5f5;
  }
`;

const GlobalContainer = styled.div`
  background-color: #fff;
  width: 100%;
  max-width: 1140px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  margin: 0 auto;
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: white;
`;

const App: React.FC = () => {
  const dispatch = useDispatch();
  const setAuthData = useAuthDataSetter();
  const customerId = useSelector((state: RootState) => state.auth.customer_id);
  const token = useSelector((state: RootState) => state.auth.accessToken);

  useEffect(() => {
    const refresh = () => {
      fetch(`${process.env.REACT_APP_BASE_API_URL}/auth/refresh-token`, {
        method: 'POST',
        credentials: 'include',
      })
      .then(res => res.json())
      .then(data => {
        if (data.accessToken) {
          dispatch(setAccessToken(data.accessToken));
          setAuthData(data);
        } else {
          dispatch(clearAccessToken());
          console.error('Refresh failed');
        }
      })
      .catch((err) => {
        console.error('Refresh error', err);
        dispatch(clearAccessToken());
      })
      .finally(() => {
        dispatch(setIsAuthChecked(true));
      });
    };

    refresh();

    const interval = setInterval(refresh, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [dispatch, setAuthData]);

  useEffect(() => {
    if (!customerId || !token) return;

    const fetchCartItems = async () => {
      
    try {
      const resCart = await fetch(
        `${process.env.REACT_APP_BASE_API_URL}/shopping-cart/customer/${customerId}?customerId=${customerId}`,
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!resCart.ok) {
        throw new Error(`Помилка отримання кошика: ${resCart.status}`);
      }
        
      const cartItems = await resCart.json();

      if (!cartItems || cartItems.length === 0) {
        return;
      }
      cartItems.forEach((item: any) => {
        dispatch(addToCart({
          id: item.book_id,
          quantity: item.book_quantity,
          item_id: item.item_id,
          customer_id: item.customer_id,
        }))
      })
    } catch (error) {
      console.error('Помилка завантаження кошика:', error);
    }
  };

  const fetchWishlist = async () => {
    try {
      const resWishlist = await fetch(`${process.env.REACT_APP_BASE_API_URL}/wishlist/customer/${customerId}?customerId=${customerId}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const wishlist = await resWishlist.json();
      if (wishlist.length) {
        wishlist.forEach((item: FavoriteItem) => {
          dispatch(addToFavorites(item));
        });
      }
    } catch (err) {
      console.error('Помилка завантаження вішліста:', err);
    }
  };
   fetchCartItems();
   fetchWishlist();
  }, [dispatch, customerId, token]);

  return (  
    <>
      <GlobalStyle />
      <GlobalContainer>
        <BrowserRouter>
          <Header />
          <ModalContainer />
          <PageContainer>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/delivery-conditions" element={<DeliveryConditions />} />
              <Route path="/store-security" element={<StoreSecurity />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />'
              <Route path="/book/:id" element={<BookDetailPage />} />
              <Route path="/verify-email" element={<EmailVerification />} />

              <Route path="/admin" element={
                <ProtectedRoute roles={['admin']}>
                  <Admin />
                </ProtectedRoute>
              } />
              <Route path="/admin/authors" element={
                <ProtectedRoute roles={['admin']}>
                  <AuthorsListView />
                </ProtectedRoute>
              } />
              <Route path="/admin/books" element={
                <ProtectedRoute roles={['admin']}>
                  <BookListView />
                </ProtectedRoute>
              } />
              <Route path="/admin/genres" element={
                <ProtectedRoute roles={['admin']}>
                  <GenresListView />
                </ProtectedRoute>
              } />
              <Route path="/admin/orders" element={
                <ProtectedRoute roles={['admin']}>
                  <OrdersListView />
                </ProtectedRoute>
              } />
              <Route path="/admin/news" element={
                <ProtectedRoute roles={['admin']}>
                  <NewsListView />
                </ProtectedRoute>
              } />
              <Route path="/admin/formats" element={
                <ProtectedRoute roles={['admin']}>
                  <FormatsListView />
                </ProtectedRoute>
              } />
              <Route path="/admin/languages" element={
                <ProtectedRoute roles={['admin']}>
                  <LanguagesListView />
                </ProtectedRoute>
              } />
              <Route path="/admin/publishers" element={
                <ProtectedRoute roles={['admin']}>
                  <PublisherListView />
                </ProtectedRoute>
              } />
              <Route path="/admin/cover-types" element={
                <ProtectedRoute roles={['admin']}>
                  <CoverTypeListView />
                </ProtectedRoute>
              } />
              <Route path="/admin/books/create" element={
                <ProtectedRoute roles={['admin']}>
                  <BookFormView />
                </ProtectedRoute>
              } />
              <Route path="/admin/books/:id" element={
                <ProtectedRoute roles={['admin']}>
                  <BookFormView />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute roles={['customer', 'admin']}>
                  <UserProfile />
                </ProtectedRoute>
              } />
              <Route path="/order" element={
                <ProtectedRoute roles={['customer', 'admin']}>
                  <OrderPage />
                </ProtectedRoute>
              } />
{/* 
<Route path="admin/tables" element={
  <ProtectedRoute roles={['admin']}>
    <DbTables />
  </ProtectedRoute>
} /> 
*/}

            </Routes>
          </PageContainer>
          <Footer />
        </BrowserRouter>
      </GlobalContainer>
    </>
  );
};

export default App;